import {
  type AdminMemberRow,
  type AdminProviderRow,
  type AdminProvidersSummary,
  formatDate,
  PROVIDER_KIND_LABELS,
  PROVIDER_REVIEW_TARGET_HOURS,
  PROVIDER_STATUSES,
  PROVINCE_LABELS,
  providerDisplayName,
  USER_STATUS_LABELS,
} from '@myhome/contracts';
import { Badge } from '@myhome/ui/components/badge';
import { Button } from '@myhome/ui/components/button';
import { Skeleton } from '@myhome/ui/components/skeleton';
import { ArrowRight, CaretRight } from '@myhome/ui/icons';
import { ListSkeleton } from '@myhome/ui/patterns/list-skeleton';
import { StatusBanner } from '@myhome/ui/patterns/status-banner';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ProviderStatusBadge } from '../components/provider-status-badge';
import { adminOverviewQuery } from '../lib/queries';

export const Route = createFileRoute('/_shell/')({
  component: OverviewPage,
});

/** The tiles whose numbers arrive with later features, and which feature brings each. */
const COMING = [
  { label: 'Jobs this week', note: 'Requested and verified, with the jobs feature' },
  { label: 'Savings to date', note: 'Reported on closed jobs, with the jobs feature' },
] as const;

/**
 * Overview: members, active subscriptions, providers by status, jobs this
 * week, savings to date (apps/admin/CLAUDE.md). Members and providers are
 * live; jobs and savings fill in with their feature. Active subscriptions join
 * the members tile with billing.
 */
function OverviewPage() {
  const result = useQuery(adminOverviewQuery());
  const members = result.data?.members;
  const providers = result.data?.providers;

  return (
    <div className="flex flex-col gap-5">
      <h1 className="font-heading text-headline font-semibold text-navy-900">Overview</h1>

      {result.isError ? (
        <StatusBanner
          tone="danger"
          title="We could not load the overview"
          description="Check your connection and try again."
          action={
            <Button variant="outline" size="sm" onClick={() => result.refetch()}>
              Try again
            </Button>
          }
        />
      ) : null}

      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <li>
          <Link
            to="/members"
            className="group flex h-full flex-col rounded-(--radius-card) border border-border bg-surface p-4 transition-colors hover:border-navy-400"
          >
            <span className="flex items-center justify-between gap-2 text-neutral-600">
              Members
              <ArrowRight
                size={16}
                aria-hidden="true"
                className="text-neutral-400 transition-colors group-hover:text-navy-900"
              />
            </span>
            {members === undefined ? (
              <>
                <Skeleton className="mt-2 h-8 w-20" />
                <Skeleton className="mt-2 h-3 w-32" />
              </>
            ) : (
              <>
                <span className="mt-2 font-heading text-headline font-bold tabular-nums text-navy-900">
                  {members.total.toLocaleString('en-ZA')}
                </span>
                <span className="mt-1 text-eyebrow text-neutral-600 tabular-nums">
                  {members.joinedLast7Days === 0
                    ? 'No one new in the last 7 days'
                    : `+${members.joinedLast7Days.toLocaleString('en-ZA')} in the last 7 days`}
                </span>
              </>
            )}
          </Link>
        </li>
        <li>
          <ProvidersTile providers={providers} />
        </li>
        {COMING.map((tile) => (
          <li key={tile.label} className="rounded-(--radius-card) border border-border bg-surface p-4">
            <p className="text-neutral-600">{tile.label}</p>
            <p className="mt-2 font-heading text-headline font-bold tabular-nums text-navy-300">--</p>
            <p className="mt-1 text-eyebrow text-neutral-500">{tile.note}</p>
          </li>
        ))}
      </ul>

      <section aria-labelledby="newest-members" className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-3">
          <h2 id="newest-members" className="font-heading text-title font-semibold text-navy-900">
            Newest members
          </h2>
          <Link
            to="/members"
            className="inline-flex min-h-(--spacing-touch) items-center font-medium text-link underline-offset-4 hover:underline"
          >
            See all members
          </Link>
        </div>
        {members === undefined ? (
          result.isError ? null : (
            <ListSkeleton count={3} shape="row" />
          )
        ) : members.recent.length === 0 ? (
          <p className="rounded-(--radius-card) border border-dashed border-border-strong bg-surface px-4 py-6 text-center text-neutral-600">
            No members yet. They appear here as soon as they register.
          </p>
        ) : (
          <ul className="overflow-hidden rounded-(--radius-card) border border-border bg-surface">
            {members.recent.map((member) => (
              <li key={member.memberNumber} className="border-t border-border first:border-t-0">
                <RecentMember member={member} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <ReviewQueue providers={providers} failed={result.isError} />

      <ProvidersByStatus providers={providers} />
    </div>
  );
}

function RecentMember({ member }: { member: AdminMemberRow }) {
  const place =
    member.mainAddress === null
      ? null
      : (member.mainAddress.suburb ?? PROVINCE_LABELS[member.mainAddress.province]);

  return (
    <Link
      to="/members/$memberNumber"
      params={{ memberNumber: member.memberNumber }}
      className="flex min-h-(--spacing-touch) items-center gap-3 px-4 py-2.5 transition-colors hover:bg-navy-50"
    >
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex flex-wrap items-center gap-2">
          <span className="truncate font-medium text-navy-900">
            {member.firstName} {member.lastName}
          </span>
          {member.status === 'ACTIVE' ? null : (
            <Badge variant="danger">{USER_STATUS_LABELS[member.status]}</Badge>
          )}
        </span>
        <span className="truncate text-eyebrow text-neutral-600">
          {[place, member.referralCode === null ? null : `Agent code ${member.referralCode}`]
            .filter((part) => part !== null)
            .join(' · ')}
        </span>
      </span>
      <span className="shrink-0 text-eyebrow text-neutral-600 tabular-nums">
        {formatDate(new Date(member.memberSince), { day: 'numeric', month: 'short' })}
      </span>
      <CaretRight size={16} aria-hidden="true" className="shrink-0 text-neutral-400" />
    </Link>
  );
}

function ProvidersTile({ providers }: { providers: AdminProvidersSummary | undefined }) {
  const waiting = providers?.byStatus.PENDING_REVIEW ?? 0;
  return (
    <Link
      to="/providers"
      className="group flex h-full flex-col rounded-(--radius-card) border border-border bg-surface p-4 transition-colors hover:border-navy-400"
    >
      <span className="flex items-center justify-between gap-2 text-neutral-600">
        Providers
        <ArrowRight
          size={16}
          aria-hidden="true"
          className="text-neutral-400 transition-colors group-hover:text-navy-900"
        />
      </span>
      {providers === undefined ? (
        <>
          <Skeleton className="mt-2 h-8 w-20" />
          <Skeleton className="mt-2 h-3 w-32" />
        </>
      ) : (
        <>
          <span className="mt-2 font-heading text-headline font-bold tabular-nums text-navy-900">
            {providers.total.toLocaleString('en-ZA')}
          </span>
          <span className="mt-1 text-eyebrow text-neutral-600 tabular-nums">
            {waiting === 0
              ? 'None waiting for review'
              : `${waiting.toLocaleString('en-ZA')} waiting for review`}
          </span>
        </>
      )}
    </Link>
  );
}

/**
 * Providers who submitted their profile, longest waiting first, each with
 * the time left on the review target (apps/admin/CLAUDE.md, providers/).
 */
function ReviewQueue({
  providers,
  failed,
}: {
  providers: AdminProvidersSummary | undefined;
  failed: boolean;
}) {
  const waiting = providers?.byStatus.PENDING_REVIEW ?? 0;
  return (
    <section aria-labelledby="review-queue" className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h2 id="review-queue" className="font-heading text-title font-semibold text-navy-900">
            Review queue
          </h2>
          <p className="text-neutral-600">
            Longest waiting first. The target is a decision within {PROVIDER_REVIEW_TARGET_HOURS} hours.
          </p>
        </div>
        {waiting === 0 ? null : (
          <Link
            to="/providers"
            search={{ status: 'PENDING_REVIEW', sort: 'submittedAt', dir: 'asc' }}
            className="inline-flex min-h-(--spacing-touch) shrink-0 items-center font-medium text-link underline-offset-4 hover:underline"
          >
            See all {waiting.toLocaleString('en-ZA')}
          </Link>
        )}
      </div>
      {providers === undefined ? (
        failed ? null : (
          <ListSkeleton count={3} shape="row" />
        )
      ) : providers.reviewQueue.length === 0 ? (
        <p className="rounded-(--radius-card) border border-dashed border-border-strong bg-surface px-4 py-6 text-center text-neutral-600">
          No providers are waiting for review. They appear here when they submit their profile.
        </p>
      ) : (
        <ul className="overflow-hidden rounded-(--radius-card) border border-border bg-surface">
          {providers.reviewQueue.map((provider) => (
            <li key={provider.id} className="border-t border-border first:border-t-0">
              <QueuedProvider provider={provider} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function QueuedProvider({ provider }: { provider: AdminProviderRow }) {
  const since = new Date(provider.submittedAt ?? provider.createdAt);
  return (
    <Link
      to="/providers/$providerId"
      params={{ providerId: provider.id }}
      className="flex min-h-(--spacing-touch) items-center gap-3 px-4 py-2.5 transition-colors hover:bg-navy-50"
    >
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium text-navy-900">{providerDisplayName(provider)}</span>
        <span className="truncate text-eyebrow text-neutral-600">
          {[
            PROVIDER_KIND_LABELS[provider.kind],
            provider.categories[0] ?? null,
            provider.basedIn?.name ?? null,
            `submitted ${formatDate(since, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`,
          ]
            .filter((part) => part !== null)
            .join(' · ')}
        </span>
      </span>
      <ReviewClock since={since} />
      <CaretRight size={16} aria-hidden="true" className="shrink-0 text-neutral-400" />
    </Link>
  );
}

/** Time left on the review target, or how far past it. Words, not colour alone. */
function ReviewClock({ since }: { since: Date }) {
  const waitedHours = Math.max(0, (Date.now() - since.getTime()) / (60 * 60 * 1000));
  const leftHours = PROVIDER_REVIEW_TARGET_HOURS - waitedHours;
  if (leftHours <= 0) {
    return (
      <Badge variant="danger" className="shrink-0 tabular-nums">
        Overdue by {formatHours(-leftHours)}
      </Badge>
    );
  }
  return (
    <Badge variant={leftHours <= 12 ? 'warning' : 'neutral'} className="shrink-0 tabular-nums">
      {formatHours(leftHours)} left
    </Badge>
  );
}

/** 40 min, 5 h, 2 days 3 h. */
function formatHours(hours: number): string {
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`;
  const whole = Math.floor(hours);
  if (whole < 24) return `${whole} h`;
  const days = Math.floor(whole / 24);
  const rest = whole % 24;
  return `${days} ${days === 1 ? 'day' : 'days'}${rest === 0 ? '' : ` ${rest} h`}`;
}

/** Every status with its count, each a link to the directory filtered to it. */
function ProvidersByStatus({ providers }: { providers: AdminProvidersSummary | undefined }) {
  if (providers === undefined) return null;
  return (
    <section aria-labelledby="providers-by-status" className="flex flex-col gap-3">
      <h2 id="providers-by-status" className="font-heading text-title font-semibold text-navy-900">
        Providers by status
      </h2>
      <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {PROVIDER_STATUSES.map((status) => (
          <li key={status}>
            <Link
              to="/providers"
              search={{ status }}
              className="flex min-h-(--spacing-touch) items-center justify-between gap-2 rounded-(--radius-card) border border-border bg-surface px-3 py-2 transition-colors hover:border-navy-400"
            >
              <ProviderStatusBadge status={status} />
              <span
                className={`font-heading font-semibold tabular-nums ${providers.byStatus[status] === 0 ? 'text-neutral-400' : 'text-navy-900'}`}
              >
                {providers.byStatus[status].toLocaleString('en-ZA')}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
